import { useTranslation } from "react-i18next"
import { useOutletContext } from "react-router"
import { UserAvatarTooltip } from "@/components/avatar-user-tooltip"
import { Banner } from "@/components/ui/banner"
import { Button } from "@/components/ui/button"
import { Dialog, DialogContentCustom, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { UserProvider } from "@/components/user-wrapper"
import { formatDateString } from "@/lib/format-date"
import { useDocumentStore } from "@/modules/patients/detail/store/useDocumentStore"
import { useCountryParam } from "@/modules/patients/hooks/useCountryParam"
import {
  CH_APPROVABLE_DOCUMENTS,
  CH_DOCUMENTS,
  DocumentViewerType,
  getDocumentTitle,
} from "@/modules/patients/types/ProgressNoteDocument"
import { SofiaEnvironment } from "@/sofia-environment"
import { ContactChannel } from "@/types/__generated__/union-types"

type DocumentViewerDialogProps = {
  type: DocumentViewerType
  onOpenChange: (open: boolean) => void
}

const DocumentViewerDialog = ({ type, onOpenChange }: DocumentViewerDialogProps) => {
  const { t } = useTranslation()
  const document = useDocumentStore((state) => state.selectedDocumentForViewing)
  const medicalReport = useDocumentStore((state) => state.selectedMedicalReportForViewing)
  const countryCode = useCountryParam()
  const { contactChannel } = useOutletContext<{
    contactChannel: ContactChannel
  }>()

  const renderMedicalReport = () => {
    if (!medicalReport) {
      return
    }

    const { url, attachmentType, fileName, uploadStatus } = medicalReport
    if (!uploadStatus) return null

    if (uploadStatus === "UPLOADED")
      return (
        <Banner type="inline" className="w-auto min-w-80">
          {t("patient.uploadFiles.messages.virusCheckInProgress")}
        </Banner>
      )
    if (uploadStatus === "MALICIOUS" || uploadStatus === "ERROR" || uploadStatus === "SCAN_TIMEOUT") {
      return (
        <Banner type="inline" variant="most-critical" className="w-auto">
          {uploadStatus === "MALICIOUS" && t("patient.uploadFiles.messages.fileUnsafeOrHarmful")}
          {uploadStatus === "ERROR" && t("patient.uploadFiles.messages.uploadFailedRetry")}
          {uploadStatus === "SCAN_TIMEOUT" && t("patient.uploadFiles.messages.virusCheckFailedRetry")}
        </Banner>
      )
    }

    const src = `${SofiaEnvironment.Bff.Url}/${url}`

    switch (attachmentType) {
      case "AUDIO":
        return <audio className="object-contain" src={src} controls />
      case "VIDEO":
        return <video className={`max-h-full w-full max-w-full object-contain`} src={src} controls />
      case "PHOTO":
        return <img className={`max-h-full w-full max-w-full object-contain`} src={src} alt={fileName} />
      case "PDF":
        return (
          <embed className="h-full w-full object-contain" src={`${src}?embed=true#view=fitH`} type="application/pdf" />
        )
    }
  }

  const renderViewer = () => {
    if (document?.fileId) {
      return (
        <div className="flex-1" key={document.fileId}>
          <embed
            className="h-full w-full"
            src={`${SofiaEnvironment.Bff.Url}/api/${countryCode.toLocaleLowerCase()}/files/${
              document.fileId
            }?embed=true#view=fitH`}
            type="application/pdf"
          />
        </div>
      )
    }

    return null
  }

  const renderSigned = () => {
    if (document?.signedAt) {
      return (
        <p className="text-on-surface-subdued text-xs tracking-[0.25px]">
          {document.type && CH_APPROVABLE_DOCUMENTS.includes(document.type)
            ? t("patient.conclude.approvedOn")
            : t("patient.conclude.signedOn")}{" "}
          {formatDateString(document.signedAt)}
        </p>
      )
    }
    return null
  }

  const renderSentDocumentInformation = () => {
    if (document?.fileId) {
      return (
        <div className="flex w-full items-center gap-2">
          <UserProvider
            userName={document.signedBy}
            render={(user) => <UserAvatarTooltip user={user} uniqueIdentifier={user.firstName} />}
          />
          {renderSigned()}
        </div>
      )
    }
    return null
  }

  const getDisplayedTextByCountry = () => {
    switch (countryCode) {
      case "DE":
        return t("patient.conclude.thisDocumentWillBeAvailableInApp")
      case "CH":
        return document?.type === CH_DOCUMENTS.REFERRAL_LETTER
          ? t("patient.referralLetter.pastDocumentInfoText")
          : t(`patient.conclude.documentSentVia.${contactChannel}`)
      default:
        return ""
    }
  }

  return (
    <Dialog open onOpenChange={onOpenChange}>
      <DialogContentCustom
        onPointerDownOutside={(e) => e.preventDefault()}
        data-testid="view-document-dialog-content"
        className="flex h-[calc(100vh-120px)] flex-col">
        <DialogHeader>
          <DialogTitle>{getDocumentTitle(t, type, countryCode)}</DialogTitle>
        </DialogHeader>
        <div className="mx-6 mb-4 flex min-h-10 items-center justify-between">
          <p className="text-on-surface text-sm"> {getDisplayedTextByCountry()}</p>
          <div className="ml-auto">{renderSentDocumentInformation()}</div>
        </div>
        <div className="mx-6 my-4 flex flex-1 flex-col overflow-hidden">
          {medicalReport ? (
            <div className="bg-background-subdued flex h-full items-center justify-center">{renderMedicalReport()}</div>
          ) : (
            renderViewer()
          )}
        </div>
        <DialogFooter className="mt-auto flex border-t p-6 pt-4" data-testid="view-document-dialog-footer">
          <Button
            className="px-4 py-2"
            variant="secondary"
            onClick={() => onOpenChange(false)}
            data-testid="cancel-button">
            {t("common.button.cancel")}
          </Button>
        </DialogFooter>
      </DialogContentCustom>
    </Dialog>
  )
}

export { DocumentViewerDialog }
